import type {PropertiesLimits} from '@github-ui/custom-properties-types'
import {getEmptyValueByType, isPropertyValueArray} from '@github-ui/custom-properties-types/helpers'
import {useDebounce} from '@github-ui/use-debounce'
import {useCallback, useEffect, useRef, useState} from 'react'

import type {DefinitionFormType} from '../helpers/definition-form'
import type {DefinitionFormSchema, FormErrors, FormMode} from '../helpers/definition-form-types'

interface Props<Schema extends DefinitionFormSchema> {
  form: DefinitionFormType<Schema>
  existingPropertyNames: string[]
  formMode: FormMode
  propertiesLimits?: PropertiesLimits
  initialValues: Schema
}

/**
 * React hook that manages state and asynchronous validation for a property-definition form.
 *
 * It initializes and maintains a normalized form state, ensures a `defaultValue` is populated
 * (based on the current `valueType` when absent), and coordinates field and form validation
 * via an internal queue with optional debouncing.
 *
 * Validation is delegated to the injected `form` adapter, which supplies the actual business
 * rules. Field updates can trigger immediate or debounced validation, or skip validation
 * altogether.
 *
 * Validation behavior:
 * - Form-level validation restricts returned `fieldErrors` to a subset of fields
 *   (`propertyName`, `description`, `defaultValue`, `regexPattern`) to avoid blocking
 *   submission due to non-relevant/transient inputs.
 * - Per-field validation can be debounced by 300ms when requested.
 */
export function usePropertyDefinitionForm<Schema extends DefinitionFormSchema>({
  form,
  initialValues,
  propertiesLimits,
  existingPropertyNames,
  formMode,
}: Props<Schema>) {
  const validationQueueRef = useRef<ValidationQueue<Schema>>(new ValidationQueue())
  const [formState, setFormState] = useState<Schema>(() => ({
    ...initialValues,
    defaultValue: initialValues.defaultValue ?? getEmptyValueByType(initialValues.valueType),
  }))
  const [allErrors, setAllErrors] = useState<FormErrors<Schema>>({formErrors: [], fieldErrors: {}})

  // Track the latest validation version for each field
  const validationVersionRef = useRef<Record<string, number>>({})

  const updateField = <K extends keyof Schema>(
    key: K,
    value: Schema[K],
    options: {skipValidation?: boolean; debounce?: boolean} = {},
  ) => {
    const optionsWithDefaults = {
      skipValidation: false,
      debounce: false,
      ...options,
    }

    setFormState(prev => ({
      ...prev,
      ...updateDependentFields(key, {
        ...prev,
        [key]: value,
      }),
    }))

    if (optionsWithDefaults.skipValidation) return

    validationQueueRef.current.add(key, optionsWithDefaults.debounce)
  }

  const validateForm = async () => {
    const result = await form.validateFormAsync(formState, {existingPropertyNames, formMode, propertiesLimits})
    // Our current implementation validates only some fields.
    // It is required to avoid preventing form submission on non relevant field errors
    // such as newAllowedValueField.
    const errors: FormErrors<Schema> = {
      ...result,
      fieldErrors: {
        propertyName: result.fieldErrors.propertyName,
        description: result.fieldErrors.description,
        defaultValue: result.fieldErrors.defaultValue,
        regexPattern: result.fieldErrors.regexPattern,
      } as Partial<Record<keyof Schema, string[]>>,
    }

    setAllErrors(errors)

    return errors
  }

  const validateFieldInternal = useCallback(
    async (key: keyof Schema, state: Schema, errors: FormErrors<Schema>) => {
      // Increment version for this field
      const fieldKey = String(key)
      const currentVersion = (validationVersionRef.current[fieldKey] ?? 0) + 1
      validationVersionRef.current[fieldKey] = currentVersion

      const result = await form.validateFieldAsync(key, state, errors, {
        existingPropertyNames,
        formMode,
        propertiesLimits,
      })
      const fieldErrors = result.fieldErrors[key]

      // Only update if this is the latest validation for this field
      if (validationVersionRef.current[fieldKey] === currentVersion) {
        setAllErrors(prev => ({
          ...prev,
          fieldErrors: {
            ...prev.fieldErrors,
            [key]: fieldErrors,
          },
        }))
      }

      return fieldErrors?.[0]
    },
    [existingPropertyNames, formMode, form, propertiesLimits],
  )

  const debounceValidateFieldInternal = useDebounce(validateFieldInternal, 300)

  useEffect(() => {
    for (const {field, debounced} of validationQueueRef.current.next()) {
      if (debounced) {
        debounceValidateFieldInternal(field, formState, allErrors)
      } else {
        validateFieldInternal(field, formState, allErrors)
      }
    }
  }, [allErrors, debounceValidateFieldInternal, formState, validateFieldInternal])

  const validateField = async (key: keyof Schema): Promise<string | undefined> => {
    return validateFieldInternal(key, formState, allErrors)
  }

  const getFieldError = (key: keyof Schema): string | undefined => {
    return allErrors.fieldErrors[key]?.[0]
  }

  const getFieldValue = <K extends keyof Schema>(key: K): Schema[K] => {
    return formState[key]
  }

  const getFormErrors = (): string[] => {
    return allErrors.formErrors
  }

  return {
    validateField,
    validateForm,
    updateField,
    getFieldValue,
    getFieldError,
    getFormErrors,
    // _* data is exposed to simplify testing
    _formState: formState,
  }
}

function updateDependentFields<S extends DefinitionFormSchema, K extends keyof S>(key: K, updatedSchema: S): S {
  const update = {...updatedSchema}
  if (key === 'regexEnabled') update.regexPattern = ''
  if (key === 'allowedValues') {
    if (isPropertyValueArray(update.defaultValue)) {
      const validValues = update.defaultValue.filter(v => updatedSchema.allowedValues.includes(v))
      update.defaultValue = validValues
    } else if (!updatedSchema.allowedValues.includes(update.defaultValue || '')) {
      update.defaultValue = ''
    }
  }
  if (key === 'valueType') {
    update.regexPattern = ''
    update.defaultValue = getEmptyValueByType(updatedSchema.valueType)
  }
  if (key === 'required' && !updatedSchema.required) {
    update.defaultValue = getEmptyValueByType(updatedSchema.valueType)
  }

  return update
}

/**
 * A per-field validation task queue for form schemas.
 *
 * Maintains a list of pending validation requests keyed by schema field. When a field
 * is enqueued again, any existing request for that field is removed and the new one is
 * appended, ensuring the most recent request for each field is processed and that there
 * is at most one pending entry per field. Items are drained in FIFO order across fields.
 *
 * Use the generator returned by {@link next} to iterate and consume all pending requests.
 */
class ValidationQueue<Schema extends DefinitionFormSchema> {
  #queue: Array<{field: keyof Schema; debounced: boolean}> = []

  add(field: keyof Schema, debounced: boolean) {
    if (this.#queue.some(item => item.field === field)) {
      this.#queue = this.#queue.filter(item => item.field !== field)
    }
    this.#queue.push({field, debounced})
  }

  *next() {
    while (this.#queue.length > 0) {
      const next = this.#queue.shift()
      if (next) yield next
    }
  }
}

const DEFAULT_FIELD_ERRORS_ORDER: Array<keyof DefinitionFormSchema> = [
  'propertyName',
  'description',
  'regexPattern',
  'newAllowedValue',
  'defaultValue',
]

/**
 * Returns the first field key (based on the provided evaluation order) that has a validation error.
 *
 * Iterates through the supplied `order` array and inspects `errors.fieldErrors` for each key.
 * The first key whose corresponding entry is truthy (i.e., an error is present) is returned.
 * If no errors are found for any keys in the given order, `undefined` is returned.
 *
 * Order falls back to a default list if none is provided, ensuring consistent behavior.
 * The default order does not account for any custom fields that may be present in the schema.
 *
 * @param errors - A structured collection of field errors indexed by schema keys.
 * @param order - The prioritized list of schema keys to check for the first invalid field.
 *
 * @returns The first key in `order` that has an associated error, or `undefined` if all are valid.
 */
export function getFirstInvalidFieldKey<Schema extends DefinitionFormSchema>(
  errors: FormErrors<Schema>,
  order: Array<keyof Schema> = DEFAULT_FIELD_ERRORS_ORDER,
): keyof Schema | undefined {
  return order.map(key => [key, errors.fieldErrors[key]]).find(([_, error]) => !!error)?.[0] as
    | keyof DefinitionFormSchema
    | undefined
}
